import React, { useMemo, useState, Dispatch, SetStateAction } from 'react';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import { GetAdMetricIndexName } from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/AdMetric/AdMetricsUtils';
import { formatDisplayName } from 'src/utils/stringFormatting';
import { adMetricDropdownOptionToEntity, AdMetricDropDownOptions } from './index';
import { useComparisonTimePeriodShortDisplayName, useAppSelector } from 'src/utils/Hooks';
import { NewBeaconExportIcon } from 'src/components/SvgIcons';
import { SlColumn } from 'src/components/BeaconV2Components/Generic/SlColumn';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import { SlRow } from 'src/components/BeaconV2Components/Generic/SlRow';
import { ExportProps } from 'src/utils/Hooks/useExportBuilder';

import { BEACON_PRO_RIGHT_CONTENT_WIDTH } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';

import { AdMetricModal } from './AdMetricModal/AdMetricModal';

export const defaultHeaderIconSpanStyle = {
  width: '24px',
  height: '24px',
  cursor: 'pointer',
  marginLeft: 'auto',
  marginBottom: 'auto'
};

export const AdvertisingMetricSubtitle = ({
  options,
  setOption,
  builderProps
}: {
  option: AdMetricDropDownOptions;
  setOption: Dispatch<SetStateAction<AdMetricDropDownOptions>>;
  options: {
    name: string;
    displayName: string;
  }[];
  builderProps: ExportProps;
}) => {
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);

  const comparisonDisplayName = useComparisonTimePeriodShortDisplayName();
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [selection, setSelection] = useState(options[0].name);
  const entityType = useMainEntityType();
  const indexName = GetAdMetricIndexName(entityType, adMetricDropdownOptionToEntity[selection].entityType);
  builderProps.indexName = indexName;

  const newOptions = useMemo(() => {
    return options.map((field) => ({
      id: field.name,
      label: field.displayName,
      ...field
    }));
  }, [options]);

  const selectedOption = useMemo(() => {
    return newOptions.find((option) => option.id === selection);
  }, [newOptions, selection]);

  return (
    <SlColumn
      widths="full"
      style={{
        paddingTop: '32px',
        maxWidth: BEACON_PRO_RIGHT_CONTENT_WIDTH
      }}
    >
      <div
        style={{
          width: '100%',
          display: 'flex'
        }}
      >
        <SlRow
          style={{
            gap: '5px',
            paddingBottom: `0px`
          }}
          verticalPosition="center"
        >
          <Text
            sx={{
              paddingBottom: `0px`,
              alignItems: 'center'
            }}
            variant="h4"
          >
            Ad Metrics by
          </Text>
          <SlDropdownMenu
            defaultLabel={selectedOption.label}
            onChange={(s) => {
              setSelection(s.id);
              setOption(s.id as any);
            }}
            selectedId={selectedOption.id}
            buttonVariant="title"
            options={newOptions}
          />
        </SlRow>
        <span style={defaultHeaderIconSpanStyle}>
          <NewBeaconExportIcon
            width="24px"
            height="24px"
            id="export"
            onClick={() => {
              setIsExportModalOpen(true);
            }}
          />
        </span>
      </div>

      <BeaconSubtitleContainer>
        <Text
          variant="body1"
          sx={{
            marginTop: '8px'
          }}
        >
          The table displays the advertising metrics by {selectedOption.displayName.toLowerCase()} during the{' '}
          {formatDisplayName(mainTimePeriod)} period compared to the {comparisonDisplayName}.
        </Text>
        <AdMetricModal
          open={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          builderProps={{
            ...builderProps
          }}
        />
      </BeaconSubtitleContainer>
    </SlColumn>
  );
};
